import { ClerkProvider } from '@clerk/react';
import { shadcn } from '@clerk/ui/themes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { shouldRetryMutation } from './api/client';
import { AuthProvider } from './auth/AuthContext';
import './index.css';
import { routeTree } from './router';

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
// Direct ingest at PostHog Cloud US. The managed reverse proxy at
// `inputrecovery.com` was tried first — it works for the same-origin
// marketing site, but every cross-origin call from `app.inputrecovery.com`
// (and from localhost dev) was blocked by CORS because the proxy doesn't
// return `Access-Control-Allow-Origin`. Direct ingest sets `ACAO: *`
// natively. Trade-off: ~5–10% ad-blocker loss vs. an authed app where
// that's effectively zero.
const posthogOptions = {
  api_host: 'https://us.i.posthog.com',
  defaults: '2026-05-30',
  // Only create person profiles after the SPA calls `posthog.identify` —
  // keeps anonymous traffic out of the user count + cheaper plan limits.
  person_profiles: 'identified_only',
  // Cookies remain local to the browser (not tied to api_host), so this
  // still shares session across `inputrecovery.com` + `app.inputrecovery.com`.
  cookie_domain: '.inputrecovery.com',
} as const;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
    // Mutations otherwise never retry, so a save/approve that hits a
    // transient 401 during a Clerk session refresh fails outright. Retry
    // once on a 401 only (no server-side effect on 401) — INP-502.
    mutations: {
      retry: shouldRetryMutation,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element missing');

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPublishableKey) {
  // Surface a visible error to the DOM rather than crashing the bundle.
  // The previous behavior threw at module-eval time, which left the page
  // blank — operators couldn't tell whether the deploy was broken or the
  // app was just slow. This renders an actionable message instead.
  console.error('VITE_CLERK_PUBLISHABLE_KEY is not set at build time.');
  rootEl.innerHTML = `
    <div style="font-family: system-ui, sans-serif; max-width: 640px; margin: 4rem auto; padding: 1.5rem; border: 1px solid #ccc; border-radius: 8px;">
      <h1 style="margin-top: 0;">App misconfigured</h1>
      <p><code>VITE_CLERK_PUBLISHABLE_KEY</code> was not set at build time.</p>
      <p>This is a deploy-time configuration issue. Set the GitHub Actions
         <code>VITE_CLERK_PUBLISHABLE_KEY</code> variable to your Clerk publishable
         key (starts with <code>pk_live_</code> or <code>pk_test_</code>) and re-deploy.</p>
    </div>
  `;
} else {
  const tree = (
    <StrictMode>
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        afterSignOutUrl="/portal"
        appearance={{ theme: shadcn }}
      >
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </StrictMode>
  );
  // PostHog is optional — when its publishable key is unset (e.g. CI's
  // placeholder build for synth-only purposes) the app renders without
  // analytics instead of crashing on a missing key.
  createRoot(rootEl).render(
    posthogKey ? (
      <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
        {tree}
      </PostHogProvider>
    ) : (
      tree
    ),
  );
}